import { useCallback, useEffect, useState } from "react";
import { Calculator, Plus } from "lucide-react";
import {
  Button,
  EmptyState,
  ListCard,
  SkeletonList,
  StatusBadge,
} from "@/design-system/components/ui";
import { formatDateInTz, formatCurrency } from "@/design-system/lib/format";
import {
  commissionModelerApi,
  type CommissionModelDto,
  type CommissionModelSummaryDto,
} from "../api/commissionModeler";
import { errorMessage } from "./commissionModeler/copy";
import { useCommissionModelerCopy } from "./commissionModeler/useCommissionModelerCopy";
import { CommissionModelFormModal } from "./commissionModeler/CommissionModelFormModal";
import { CommissionModelWorkup } from "./commissionModeler/CommissionModelWorkup";

// Broker Commission Modeler — the broker's own what-if scratchpad.
//
// A SHELL, like PrequalificationTab. It owns four things:
//
//   1. The saved-model list, loaded on mount.
//   2. Which model (if any) is opened as a full workup.
//   3. The form modal, in both its create and its edit mode.
//   4. Delete.
//
// Everything that renders a figure lives in ./commissionModeler/, and every
// figure it renders came from the server. This file computes nothing.
//
// The workup replaces the list rather than sitting beside it, because a workup
// is a long scrolling document (a full amortization schedule) rather than a
// glance — the drawer treatment the prequalification drill-in uses would put a
// 240-row table in a side panel.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query (plain useState loads, mirroring the other
// portal tabs). The write goes through useSubmittingMutation inside the modal.

export function CommissionModelerTab() {
  const [models, setModels] = useState<CommissionModelSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The open workup. Held as the FULL dto rather than an id: the list summary
  // carries no schedule, so opening one is a read either way, and holding the
  // read result is what lets a save drop straight into the workup without a
  // second round trip.
  const [open, setOpen] = useState<CommissionModelDto | null>(null);
  const [opening, setOpening] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  /** The model the form is editing, or null for a new one. */
  const [editing, setEditing] = useState<CommissionModelDto | null>(null);

  const [deleting, setDeleting] = useState(false);

  // The form's admin-editable vocabulary. Resolved once here rather than inside
  // the modal (which stays mounted while closed, so it would fetch on every tab
  // load regardless) and never pending — it starts at the compiled-in copy and
  // swaps when the server's lands, so there is nothing to gate rendering on.
  const copy = useCommissionModelerCopy();

  const load = useCallback(async () => {
    try {
      const rows = await commissionModelerApi.list();
      setModels(rows);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "Couldn't load your models."));
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await commissionModelerApi.list();
        if (!cancelled) setModels(rows);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your models."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const openModel = useCallback(async (id: string) => {
    setOpening(true);
    setError(null);
    try {
      const dto = await commissionModelerApi.get(id);
      setOpen(dto);
    } catch (err) {
      setError(errorMessage(err, "Couldn't open that model."));
    } finally {
      setOpening(false);
    }
  }, []);

  const handleSaved = useCallback(
    async (saved: CommissionModelDto) => {
      // The saved dto carries its own freshly computed workup, so land on it
      // directly — a broker who just described a deal wants the answer, not the
      // list they were on a moment ago.
      setOpen(saved);
      await load();
    },
    [load],
  );

  const handleDelete = useCallback(async () => {
    if (!open) return;
    setDeleting(true);
    try {
      await commissionModelerApi.remove(open.id);
      setOpen(null);
      await load();
    } catch (err) {
      setError(errorMessage(err, "Couldn't delete that model."));
    } finally {
      setDeleting(false);
    }
  }, [open, load]);

  const startNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const startEdit = () => {
    setEditing(open);
    setFormOpen(true);
  };

  const body = () => {
    if (open) {
      return (
        <CommissionModelWorkup
          model={open}
          onBack={() => setOpen(null)}
          onEdit={startEdit}
          onDelete={() => void handleDelete()}
          deleting={deleting}
        />
      );
    }

    if (!models) return <SkeletonList rows={3} data-testid="commission-modeler-loading" />;

    if (models.length === 0) {
      return (
        <EmptyState
          icon={Calculator}
          title="No models yet"
          description="Sketch a deal — its size, the down payment, and how you'd be paid — and see what it earns you at closing and every month. Nothing you model here is submitted to Phoenix or attached to a deal."
          action={{ label: "New Model", onClick: startNew }}
        />
      );
    }

    return (
      <div className="flex flex-col gap-space-2" data-testid="commission-modeler-list">
        {models.map((m) => (
          <ListCard
            key={m.id}
            title={
              <button
                type="button"
                onClick={() => void openModel(m.id)}
                className="text-left text-onyx hover:underline"
                data-testid={`commission-model-row-${m.id}`}
              >
                {m.name}
              </button>
            }
            meta={
              <>
                {m.clientName ? `${m.clientName} · ` : ""}
                {formatCurrency(m.totalDealSize)} deal
                {" · saved "}
                {formatDateInTz(m.modifiedAt ?? m.createdAt)}
              </>
            }
            status={
              <StatusBadge tone="progress">
                {formatCurrency(m.lifetimeTotal)} to you
              </StatusBadge>
            }
          />
        ))}
      </div>
    );
  };

  return (
    <section aria-label="Commission Modeler" className="flex flex-col gap-space-4">
      {!open && (
        <div className="flex flex-wrap items-center justify-between gap-space-3">
          <p className="m-0 max-w-reading text-small text-gray-mid">
            Private to you. Model how a deal would pay you before you talk terms
            &mdash; nothing here is submitted, attached to a deal, or seen by
            anyone else.
          </p>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={startNew}
            data-testid="commission-modeler-new"
          >
            <Plus size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
            New Model
          </Button>
        </div>
      )}

      {error && (
        <p className="m-0 text-small text-status-error" role="alert">
          {error}
        </p>
      )}
      {opening && !open && (
        <p className="m-0 text-caption text-gray-mid" role="status">
          Opening…
        </p>
      )}

      {body()}

      <CommissionModelFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        model={editing}
        onSaved={handleSaved}
        copy={copy}
      />
    </section>
  );
}
